import React, { useState } from 'react';
import { HelpCircle, Plus, Trash2 } from 'lucide-react';
import {
  Child529Plan,
  CollegeCostPreset,
  DebtKind,
  DebtObligation,
  FinancialPlanData,
  IncomePhase,
  LgcEditorGroup,
} from '../../types/financialPlan';
import {
  COLLEGE_COST_PRESETS,
  DEBT_KIND_LABELS,
  EMERGENCY_FUND_COVERAGE_MONTHS,
  FIELD_HINTS,
  computeMonthlyExpenses,
  defaultK12Fields,
  emergencyFundFromMonthlyExpenses,
  DEFAULT_PHASE_GROSS,
  grossIncomeTodayAtAge,
  livingExpensesFromBudget,
  monthlyTithingAmount,
  afterTaxMonthlyIncome,
  syncCurrentFinancesExpenses,
  newChildId,
  newDebtId,
  newGoalLineId,
  newBudgetCategoryId,
  newIncomePhaseId,
  newSpendingCategoryId,
  syncRetirementSpending,
} from '../../utils/financialPlanDefaults';
import LgcAccountsSection from './LgcAccountsSection';
import {
  ageAtDate,
  formatDisplayDate,
  formatISODateLocal,
  isValidISODate,
  planAsOfDate,
} from '../../utils/planDates';
import {
  computeRetirementCorpus,
  formatCurrency,
  projectDebtPayoffAge,
  retirementMonthlyFromTodayAnnual,
  totalRetirementSpendingToday,
  validateIncomePhases,
  yearsToRetirement,
} from '../../utils/lifetimeGoalsCalc';

type UpdateFn = (patch: Partial<FinancialPlanData>) => void;

function FieldLabel({ id, label, tooltip }: { id: string; label: string; tooltip?: string }) {
  return (
    <label htmlFor={id} className="lgc-field-label">
      <span>{label}</span>
      {tooltip && (
        <span className="lgc-tooltip-wrap">
          <button
            type="button"
            className="lgc-tooltip-trigger"
            aria-label={`About ${label}`}
            aria-describedby={`${id}-tooltip`}
          >
            <HelpCircle size={13} aria-hidden />
          </button>
          <span id={`${id}-tooltip`} className="lgc-tooltip" role="tooltip">
            {tooltip}
          </span>
        </span>
      )}
    </label>
  );
}

function NumField({
  id,
  label,
  value,
  onChange,
  hint,
  tooltip,
  min = 0,
  max,
  step = 1,
  prefix,
  suffix,
  disabled,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (v: number) => void;
  hint?: string;
  tooltip?: string;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  disabled?: boolean;
}) {
  return (
    <div className={`lgc-field${label ? '' : ' lgc-field--no-label'}`}>
      {label ? <FieldLabel id={id} label={label} tooltip={tooltip} /> : null}
      <div className={`lgc-field-input-wrap${disabled ? ' lgc-field-input-wrap--readonly' : ''}`}>
        {prefix && <span className="lgc-field-affix">{prefix}</span>}
        <input
          id={id}
          type="number"
          className="lgc-field-input"
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          value={Number.isFinite(value) ? value : ''}
          onChange={(e) => onChange(Math.max(min, parseFloat(e.target.value) || 0))}
        />
        {suffix && <span className="lgc-field-affix">{suffix}</span>}
      </div>
      {hint && <p className="lgc-hint">{hint}</p>}
    </div>
  );
}

function DateField({
  id,
  label,
  value,
  onChange,
  hint,
  tooltip,
  max,
}: {
  id: string;
  label: string;
  value?: string;
  onChange: (v: string | undefined) => void;
  hint?: string;
  tooltip?: string;
  max?: string;
}) {
  return (
    <div className="lgc-field">
      <FieldLabel id={id} label={label} tooltip={tooltip} />
      <input
        id={id}
        type="date"
        className="lgc-field-input"
        value={value ?? ''}
        max={max ?? formatISODateLocal(new Date())}
        onChange={(e) => onChange(e.target.value || undefined)}
      />
      {hint && <p className="lgc-hint">{hint}</p>}
    </div>
  );
}

function healthIsActive(health: FinancialPlanData['health']): boolean {
  return (
    health.preMedicareMonthlyPremium > 0 ||
    health.hsaAnnualContribution > 0 ||
    health.deductibleBufferTarget > 0 ||
    health.yearsOfCoverageNeeded > 0
  );
}

function lifestyleIsActive(lifestyle: FinancialPlanData['lifestyle']): boolean {
  return (
    lifestyle.vacationsPerYear > 0 ||
    lifestyle.vacationCost > 0 ||
    lifestyle.otherGoals.some((g) => g.annualAmount > 0 || g.label.trim() !== '')
  );
}

interface LgcSectionContentProps {
  group: LgcEditorGroup;
  plan: FinancialPlanData;
  sourcePlan?: FinancialPlanData;
  onUpdate: UpdateFn;
}

const LgcSectionContent: React.FC<LgcSectionContentProps> = ({ group, plan, sourcePlan, onUpdate }) => {
  const planForEdits = sourcePlan ?? plan;
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [showPhases, setShowPhases] = useState(plan.income.phases.length > 1);
  const [showHealth, setShowHealth] = useState(healthIsActive(plan.health));
  const [showLifestyle, setShowLifestyle] = useState(lifestyleIsActive(plan.lifestyle));

  const updateProfile = (patch: Partial<FinancialPlanData['profile']>) =>
    onUpdate({ profile: { ...plan.profile, ...patch } });
  const updateCurrent = (patch: Partial<FinancialPlanData['currentFinances']>) => {
    const nextFinances = { ...plan.currentFinances, ...patch };
    const grossNow = grossIncomeTodayAtAge(plan.profile, plan.income, plan.profile.currentAge);
    nextFinances.monthlyExpenses = computeMonthlyExpenses(
      nextFinances.budgetCategories,
      nextFinances.tithingPct,
      grossNow,
      plan.profile.effectiveTaxRatePct,
    );
    if (nextFinances.autoEmergencyFund6Mo) {
      nextFinances.emergencyFundTarget = emergencyFundFromMonthlyExpenses(
        livingExpensesFromBudget(nextFinances.budgetCategories),
      );
    }
    onUpdate({ currentFinances: nextFinances });
  };

  const setAutoEmergencyFund = (auto: boolean) => {
    if (auto) {
      updateCurrent({
        autoEmergencyFund6Mo: true,
        emergencyFundTarget: emergencyFundFromMonthlyExpenses(
          livingExpensesFromBudget(plan.currentFinances.budgetCategories),
        ),
      });
    } else {
      updateCurrent({ autoEmergencyFund6Mo: false });
    }
  };

  const updateBudgetCategory = (id: string, patch: Partial<{ label: string; monthlyAmount: number }>) => {
    const budgetCategories = plan.currentFinances.budgetCategories.map((c) =>
      c.id === id ? { ...c, ...patch } : c,
    );
    updateCurrent({ budgetCategories });
  };

  const addBudgetCategory = () => {
    updateCurrent({
      budgetCategories: [
        ...plan.currentFinances.budgetCategories,
        { id: newBudgetCategoryId(), label: 'New category', monthlyAmount: 0 },
      ],
    });
  };

  const removeBudgetCategory = (id: string) => {
    updateCurrent({
      budgetCategories: plan.currentFinances.budgetCategories.filter((c) => c.id !== id),
    });
  };
  const updateRetirement = (patch: Partial<FinancialPlanData['retirement']>) => {
    const next = syncRetirementSpending({ ...plan.retirement, ...patch, mode: 'expenses' });
    onUpdate({ retirement: next });
  };

  const updateSpendingCategory = (id: string, patch: Partial<{ label: string; annualAmountToday: number }>) => {
    const spendingCategories = plan.retirement.spendingCategories.map((c) =>
      c.id === id ? { ...c, ...patch } : c,
    );
    updateRetirement({ spendingCategories });
  };

  const addSpendingCategory = () => {
    updateRetirement({
      spendingCategories: [
        ...plan.retirement.spendingCategories,
        { id: newSpendingCategoryId(), label: 'New category', annualAmountToday: 0 },
      ],
    });
  };

  const removeSpendingCategory = (id: string) => {
    updateRetirement({
      spendingCategories: plan.retirement.spendingCategories.filter((c) => c.id !== id),
    });
  };
  const updateHealth = (patch: Partial<FinancialPlanData['health']>) =>
    onUpdate({ health: { ...plan.health, ...patch } });
  const updateLifestyle = (patch: Partial<FinancialPlanData['lifestyle']>) =>
    onUpdate({ lifestyle: { ...plan.lifestyle, ...patch } });

  const updateChild = (id: string, patch: Partial<Child529Plan>) => {
    onUpdate({
      children: plan.children.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    });
  };

  const addChild = () => {
    onUpdate({
      children: [
        ...plan.children,
        {
          id: newChildId(),
          name: `Child ${plan.children.length + 1}`,
          currentAge: 5,
          collegeStartAge: 18,
          costPreset: 'public_in_state',
          annualCost: COLLEGE_COST_PRESETS.public_in_state.annualCost,
          balance529: 0,
          monthlyContribution529: 0,
          yearsOfCollege: 4,
          ...defaultK12Fields(),
        },
      ],
    });
  };

  const removeChild = (id: string) => {
    onUpdate({ children: plan.children.filter((c) => c.id !== id) });
  };

  const updateIncome = (patch: Partial<FinancialPlanData['income']>) => {
    const nextPlan = { ...plan, income: { ...plan.income, ...patch } };
    onUpdate({
      income: nextPlan.income,
      currentFinances: syncCurrentFinancesExpenses(nextPlan),
    });
  };

  const updateIncomePhase = (id: string, patch: Partial<IncomePhase>) => {
    updateIncome({
      phases: plan.income.phases.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    });
  };

  const addIncomePhase = () => {
    const last = plan.income.phases[plan.income.phases.length - 1];
    const startAge = last ? last.endAge + 1 : plan.profile.currentAge;
    setShowPhases(true);
    updateIncome({
      phases: [
        ...plan.income.phases,
        {
          id: newIncomePhaseId(),
          label: `Phase ${plan.income.phases.length + 1}`,
          startAge,
          endAge: Math.min(plan.profile.retirementAge - 1, startAge + 9),
          grossAnnualIncome: last?.grossAnnualIncome ?? DEFAULT_PHASE_GROSS,
          annualBonus: 0,
          withinPhaseGrowthPct: 0,
        },
      ],
    });
  };

  const removeIncomePhase = (id: string) => {
    const next = plan.income.phases.filter((p) => p.id !== id);
    if (next.length <= 1) setShowPhases(false);
    updateIncome({ phases: next });
  };

  const updateDebt = (id: string, patch: Partial<DebtObligation>) => {
    onUpdate({
      debts: plan.debts.map((d) => (d.id === id ? { ...d, ...patch } : d)),
    });
  };

  const addDebt = () => {
    onUpdate({
      debts: [
        ...plan.debts,
        {
          id: newDebtId(),
          label: 'New debt',
          kind: 'personal' as DebtKind,
          balance: 0,
          interestRatePct: 0,
          monthlyPayment: 0,
          startAge: plan.profile.currentAge,
        },
      ],
    });
  };

  const removeDebt = (id: string) => {
    onUpdate({ debts: plan.debts.filter((d) => d.id !== id) });
  };

  const setChildPreset = (id: string, preset: CollegeCostPreset) => {
    const cost = preset === 'custom'
      ? plan.children.find((c) => c.id === id)?.annualCost ?? 0
      : COLLEGE_COST_PRESETS[preset].annualCost;
    updateChild(id, { costPreset: preset, annualCost: cost });
  };

  const corpusAtRetirement = computeRetirementCorpus(plan.profile, plan.retirement);
  const yearsToRetire = yearsToRetirement(plan.profile.currentAge, plan.profile.retirementAge);
  const spendingTotalToday = totalRetirementSpendingToday(plan.retirement);
  const spendingTotalTodayMonthly = spendingTotalToday / 12;

  const budgetSubtotal = plan.currentFinances.budgetCategories.reduce(
    (s, c) => s + Math.max(0, c.monthlyAmount),
    0,
  );
  const grossAtCurrentAge = grossIncomeTodayAtAge(
    plan.profile,
    plan.income,
    plan.profile.currentAge,
  );
  const monthlyTithing = monthlyTithingAmount(
    grossAtCurrentAge,
    plan.profile.effectiveTaxRatePct,
    plan.currentFinances.tithingPct,
  );
  const afterTaxMonthly = afterTaxMonthlyIncome(
    grossAtCurrentAge,
    plan.profile.effectiveTaxRatePct,
  );
  const planAsOf = planAsOfDate(plan.profile, new Date());
  const planAsOfLabel = formatDisplayDate(formatISODateLocal(planAsOf));
  const singlePhase = plan.income.phases[0];
  const useSimpleIncome = !showPhases && plan.income.phases.length <= 1 && singlePhase;

  if (group === 'you') {
    return (
      <div className="lgc-field-grid">
        <DateField
          id="birthdate"
          label="Your birthday"
          value={plan.profile.birthDate}
          onChange={(v) => {
            updateProfile({
              birthDate: v,
              ...(v ? { currentAge: ageAtDate(v, planAsOf) } : {}),
            });
          }}
        />
        {plan.profile.birthDate && isValidISODate(plan.profile.birthDate) ? (
          <div className="lgc-field">
            <FieldLabel id="age-derived" label="Your age" />
            <p className="lgc-preview-value lgc-mono">{plan.profile.currentAge}</p>
            <p className="lgc-hint">As of {planAsOfLabel}</p>
          </div>
        ) : (
          <NumField
            id="age"
            label="Your age"
            value={plan.profile.currentAge}
            onChange={(v) => updateProfile({ currentAge: v })}
            hint="Add a birthday to keep age current."
          />
        )}
        <DateField
          id="spouse-birthdate"
          label="Spouse birthday"
          value={plan.profile.spouseBirthDate}
          onChange={(v) => {
            updateProfile({
              spouseBirthDate: v,
              ...(v ? { spouseAge: ageAtDate(v, planAsOf) } : {}),
            });
          }}
        />
        {plan.profile.spouseBirthDate && isValidISODate(plan.profile.spouseBirthDate) ? (
          <div className="lgc-field">
            <FieldLabel id="spouse-age-derived" label="Spouse age" />
            <p className="lgc-preview-value lgc-mono">{plan.profile.spouseAge}</p>
          </div>
        ) : (
          <NumField
            id="spouse-age"
            label="Spouse age"
            value={plan.profile.spouseAge}
            onChange={(v) => updateProfile({ spouseAge: v })}
          />
        )}
        <NumField
          id="ret-age"
          label="Retire at age"
          value={plan.profile.retirementAge}
          onChange={(v) => updateProfile({ retirementAge: v })}
        />

        <button
          type="button"
          className="lgc-advanced-toggle lgc-field--full"
          onClick={() => setShowAssumptions((v) => !v)}
        >
          {showAssumptions ? 'Hide assumptions' : 'Assumptions'}
        </button>
        {showAssumptions && (
          <>
            <DateField
              id="plan-start"
              label="Plan start date"
              value={plan.profile.planStartDate}
              onChange={(v) => updateProfile({ planStartDate: v })}
              hint={FIELD_HINTS.planStartDate}
            />
            <NumField
              id="inflation"
              label="Inflation"
              suffix="%"
              step={0.1}
              value={plan.profile.inflationPct}
              onChange={(v) => updateProfile({ inflationPct: v })}
            />
            <NumField
              id="return"
              label="Portfolio return"
              suffix="%"
              step={0.1}
              value={plan.profile.portfolioReturnPct}
              onChange={(v) => updateProfile({ portfolioReturnPct: v })}
            />
            <NumField
              id="tax"
              label="Effective tax rate"
              suffix="%"
              step={0.1}
              value={plan.profile.effectiveTaxRatePct}
              onChange={(v) => updateProfile({ effectiveTaxRatePct: v })}
            />
            <NumField
              id="swr"
              label="Safe withdrawal rate"
              suffix="%"
              step={0.1}
              value={plan.profile.safeWithdrawalRatePct}
              onChange={(v) => updateProfile({ safeWithdrawalRatePct: v })}
            />
          </>
        )}
      </div>
    );
  }

  if (group === 'money') {
    const phaseValidation = validateIncomePhases(
      plan.income.phases,
      plan.profile.currentAge,
      plan.profile.retirementAge,
    );
    return (
      <div className="lgc-field-grid">
        {useSimpleIncome ? (
          <>
            <NumField
              id="income-simple"
              label="Annual income"
              prefix="$"
              value={singlePhase.grossAnnualIncome}
              onChange={(v) => updateIncomePhase(singlePhase.id, { grossAnnualIncome: v })}
              hint="Today's dollars"
            />
            <NumField
              id="bonus-simple"
              label="Annual bonus"
              prefix="$"
              value={singlePhase.annualBonus ?? 0}
              onChange={(v) => updateIncomePhase(singlePhase.id, { annualBonus: v })}
            />
          </>
        ) : (
          <>
            {(phaseValidation.overlaps.length > 0 || phaseValidation.gaps.length > 0) && (
              <div className="lgc-preview-box lgc-field--full">
                {phaseValidation.overlaps.length > 0 && (
                  <p className="lgc-hint lgc-hint--warn">Overlapping phases — only one applies per age.</p>
                )}
                {phaseValidation.gaps.map((g) => (
                  <p key={`${g.fromAge}-${g.toAge}`} className="lgc-hint lgc-hint--warn">
                    Income gap from age {g.fromAge} to {g.toAge}.
                  </p>
                ))}
              </div>
            )}
            <div className="lgc-budget-table lgc-field--full">
                <div className="lgc-budget-header lgc-income-phase-header lgc-income-phase-row--simple">
                <span>Phase</span>
                <span>Start</span>
                <span>End</span>
                <span>Gross /yr</span>
                <span className="lgc-sr-only">Remove</span>
              </div>
              {plan.income.phases.map((phase) => (
                <div key={phase.id} className="lgc-budget-row lgc-income-phase-row lgc-income-phase-row--simple">
                  <input
                    className="lgc-spending-label"
                    aria-label="Phase label"
                    value={phase.label ?? ''}
                    onChange={(e) => updateIncomePhase(phase.id, { label: e.target.value })}
                  />
                  <input
                    type="number"
                    className="lgc-field-input"
                    min={0}
                    aria-label="Start age"
                    value={phase.startAge || ''}
                    onChange={(e) =>
                      updateIncomePhase(phase.id, { startAge: Math.max(0, parseFloat(e.target.value) || 0) })
                    }
                  />
                  <input
                    type="number"
                    className="lgc-field-input"
                    min={0}
                    aria-label="End age"
                    value={phase.endAge || ''}
                    onChange={(e) =>
                      updateIncomePhase(phase.id, { endAge: Math.max(0, parseFloat(e.target.value) || 0) })
                    }
                  />
                  <div className="lgc-field-input-wrap lgc-spending-input">
                    <span className="lgc-field-affix">$</span>
                    <input
                      type="number"
                      className="lgc-field-input"
                      min={0}
                      aria-label="Gross annual income"
                      value={phase.grossAnnualIncome || ''}
                      onChange={(e) =>
                        updateIncomePhase(phase.id, {
                          grossAnnualIncome: Math.max(0, parseFloat(e.target.value) || 0),
                        })
                      }
                    />
                  </div>
                  <button
                    type="button"
                    className="lgc-icon-btn"
                    onClick={() => removeIncomePhase(phase.id)}
                    aria-label={`Remove ${phase.label ?? 'phase'}`}
                    disabled={plan.income.phases.length <= 1}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
        <button type="button" className="lgc-add-btn lgc-field--full" onClick={addIncomePhase}>
          <Plus size={14} /> Add a later career phase
        </button>

        <NumField
          id="tithing"
          label="Tithing"
          suffix="%"
          step={0.5}
          max={100}
          value={plan.currentFinances.tithingPct}
          onChange={(v) => updateCurrent({ tithingPct: Math.min(100, v) })}
          hint={
            plan.currentFinances.tithingPct > 0
              ? `${formatCurrency(monthlyTithing)}/mo of ${formatCurrency(afterTaxMonthly)} after-tax`
              : undefined
          }
        />

        <div className="lgc-budget-table lgc-field--full">
          <div className="lgc-budget-header">
            <span>Monthly spending</span>
            <span>Amount</span>
            <span className="lgc-sr-only">Remove</span>
          </div>
          {plan.currentFinances.budgetCategories.map((cat) => (
            <div key={cat.id} className="lgc-budget-row">
              <input
                className="lgc-spending-label"
                aria-label="Budget category name"
                value={cat.label}
                onChange={(e) => updateBudgetCategory(cat.id, { label: e.target.value })}
              />
              <div className="lgc-field-input-wrap lgc-spending-input">
                <span className="lgc-field-affix">$</span>
                <input
                  type="number"
                  className="lgc-field-input"
                  min={0}
                  step={50}
                  aria-label={`${cat.label} monthly amount`}
                  value={cat.monthlyAmount || ''}
                  onChange={(e) =>
                    updateBudgetCategory(cat.id, {
                      monthlyAmount: Math.max(0, parseFloat(e.target.value) || 0),
                    })
                  }
                />
              </div>
              <button
                type="button"
                className="lgc-icon-btn"
                onClick={() => removeBudgetCategory(cat.id)}
                aria-label={`Remove ${cat.label}`}
                disabled={plan.currentFinances.budgetCategories.length <= 1}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div className="lgc-budget-row lgc-budget-row--total">
            <span className="lgc-spending-total-label">Monthly living</span>
            <span className="lgc-mono lgc-budget-total-value">
              {formatCurrency(budgetSubtotal + monthlyTithing)}/mo
            </span>
            <span />
          </div>
        </div>
        <button type="button" className="lgc-add-btn lgc-field--full" onClick={addBudgetCategory}>
          <Plus size={14} /> Add spending line
        </button>

        <div className="lgc-field lgc-field--full lgc-emergency-field">
          <div className="lgc-emergency-header">
            <FieldLabel id="emergency" label="Emergency fund" />
            <div className="lgc-auto-toggle">
              <span className="lgc-auto-toggle-label">{EMERGENCY_FUND_COVERAGE_MONTHS}× expenses</span>
              <button
                type="button"
                role="switch"
                aria-checked={plan.currentFinances.autoEmergencyFund6Mo}
                aria-label={`Auto-calculate emergency fund as ${EMERGENCY_FUND_COVERAGE_MONTHS} months of living expenses`}
                className={`lgc-auto-toggle-btn${plan.currentFinances.autoEmergencyFund6Mo ? ' on' : ''}`}
                onClick={() => setAutoEmergencyFund(!plan.currentFinances.autoEmergencyFund6Mo)}
              >
                <span className="lgc-auto-toggle-knob" />
              </button>
            </div>
          </div>
          {!plan.currentFinances.autoEmergencyFund6Mo && (
            <NumField
              id="emergency-amount"
              label=""
              prefix="$"
              value={plan.currentFinances.emergencyFundTarget}
              onChange={(v) => updateCurrent({ emergencyFundTarget: v, autoEmergencyFund6Mo: false })}
            />
          )}
          {plan.currentFinances.autoEmergencyFund6Mo && (
            <p className="lgc-hint">
              {formatCurrency(plan.currentFinances.emergencyFundTarget)} target
            </p>
          )}
        </div>

        {plan.debts.map((debt) => {
          const payoffAge = projectDebtPayoffAge(debt, plan.profile);
          return (
            <div key={debt.id} className="lgc-child-card lgc-field--full">
              <div className="lgc-child-header">
                <input
                  className="lgc-child-name"
                  aria-label="Debt label"
                  value={debt.label}
                  onChange={(e) => updateDebt(debt.id, { label: e.target.value })}
                />
                <button
                  type="button"
                  className="lgc-icon-btn"
                  onClick={() => removeDebt(debt.id)}
                  aria-label={`Remove ${debt.label}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="lgc-field-grid lgc-field-grid--2">
                <div className="lgc-field">
                  <label htmlFor={`${debt.id}-kind`} className="lgc-field-label">Type</label>
                  <select
                    id={`${debt.id}-kind`}
                    className="lgc-select"
                    value={debt.kind}
                    onChange={(e) => updateDebt(debt.id, { kind: e.target.value as DebtKind })}
                  >
                    {Object.entries(DEBT_KIND_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <NumField
                  id={`${debt.id}-bal`}
                  label="Balance"
                  prefix="$"
                  value={debt.balance}
                  onChange={(v) => updateDebt(debt.id, { balance: v })}
                />
                <NumField
                  id={`${debt.id}-pmt`}
                  label="Monthly payment"
                  prefix="$"
                  value={debt.monthlyPayment}
                  onChange={(v) => updateDebt(debt.id, { monthlyPayment: v })}
                />
                <NumField
                  id={`${debt.id}-rate`}
                  label="Rate"
                  suffix="%"
                  step={0.1}
                  value={debt.interestRatePct}
                  onChange={(v) => updateDebt(debt.id, { interestRatePct: v })}
                />
                {payoffAge != null && debt.balance > 0 && debt.monthlyPayment > 0 && (
                  <p className="lgc-hint lgc-field--full">Paid off at age {payoffAge}.</p>
                )}
              </div>
            </div>
          );
        })}
        <button type="button" className="lgc-add-btn lgc-field--full" onClick={addDebt}>
          <Plus size={14} /> Add a loan
        </button>
      </div>
    );
  }

  if (group === 'goals') {
    return (
      <div className="lgc-field-grid">
        {plan.children.map((child) => {
          const showK12Bands = child.primaryAnnualCostToday > 0 || child.highSchoolAnnualCostToday > 0;
          return (
            <div key={child.id} className="lgc-child-card lgc-field--full">
              <div className="lgc-child-header">
                <input
                  className="lgc-child-name"
                  aria-label="Child name"
                  value={child.name}
                  onChange={(e) => updateChild(child.id, { name: e.target.value })}
                />
                <button type="button" className="lgc-icon-btn" onClick={() => removeChild(child.id)} aria-label={`Remove ${child.name}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="lgc-field-grid lgc-field-grid--2">
                <DateField
                  id={`${child.id}-birthdate`}
                  label="Birthday"
                  value={child.birthDate}
                  onChange={(v) =>
                    updateChild(child.id, {
                      birthDate: v,
                      ...(v ? { currentAge: ageAtDate(v, planAsOf) } : {}),
                    })
                  }
                />
                {child.birthDate && isValidISODate(child.birthDate) ? (
                  <div className="lgc-field">
                    <FieldLabel id={`${child.id}-age-derived`} label="Age" />
                    <p className="lgc-preview-value lgc-mono">{child.currentAge}</p>
                  </div>
                ) : (
                  <NumField
                    id={`${child.id}-age`}
                    label="Age"
                    value={child.currentAge}
                    onChange={(v) => updateChild(child.id, { currentAge: v })}
                  />
                )}
                <div className="lgc-field lgc-field--full">
                  <span className="lgc-field-label">College cost</span>
                  <div className="lgc-chip-row" role="group" aria-label="College cost preset">
                    {Object.entries(COLLEGE_COST_PRESETS).map(([k, v]) => (
                      <button
                        key={k}
                        type="button"
                        className={`ops-estimate-chip${child.costPreset === k ? ' active' : ''}`}
                        onClick={() => setChildPreset(child.id, k as CollegeCostPreset)}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
                <NumField
                  id={`${child.id}-cost`}
                  label="Annual college cost"
                  prefix="$"
                  value={child.annualCost}
                  onChange={(v) => updateChild(child.id, { annualCost: v, costPreset: 'custom' })}
                />
                <NumField
                  id={`${child.id}-529`}
                  label="529 balance"
                  prefix="$"
                  value={child.balance529}
                  onChange={(v) => updateChild(child.id, { balance529: v })}
                />
                <NumField
                  id={`${child.id}-529mo`}
                  label="529 monthly"
                  prefix="$"
                  value={child.monthlyContribution529}
                  onChange={(v) => updateChild(child.id, { monthlyContribution529: v })}
                />
                <NumField
                  id={`${child.id}-k12-primary`}
                  label="K–12 annual cost"
                  prefix="$"
                  value={child.primaryAnnualCostToday}
                  onChange={(v) => updateChild(child.id, { primaryAnnualCostToday: v })}
                />
                <NumField
                  id={`${child.id}-k12-hs`}
                  label="High school annual cost"
                  prefix="$"
                  value={child.highSchoolAnnualCostToday}
                  onChange={(v) => updateChild(child.id, { highSchoolAnnualCostToday: v })}
                />
                {showK12Bands && (
                  <>
                    <NumField
                      id={`${child.id}-k12-p-start`}
                      label="Primary start age"
                      value={child.primaryStartAge}
                      onChange={(v) => updateChild(child.id, { primaryStartAge: v })}
                    />
                    <NumField
                      id={`${child.id}-k12-p-end`}
                      label="Primary end age"
                      value={child.primaryEndAge}
                      onChange={(v) => updateChild(child.id, { primaryEndAge: v })}
                    />
                    <NumField
                      id={`${child.id}-k12-hs-start`}
                      label="High school start age"
                      value={child.highSchoolStartAge}
                      onChange={(v) => updateChild(child.id, { highSchoolStartAge: v })}
                    />
                    <NumField
                      id={`${child.id}-k12-hs-end`}
                      label="High school end age"
                      value={child.highSchoolEndAge}
                      onChange={(v) => updateChild(child.id, { highSchoolEndAge: v })}
                    />
                  </>
                )}
              </div>
            </div>
          );
        })}
        <button type="button" className="lgc-add-btn lgc-field--full" onClick={addChild}>
          <Plus size={14} /> Add child
        </button>

        {showHealth ? (
          <div className="lgc-child-card lgc-field--full">
            <div className="lgc-child-header">
              <h3 className="lgc-inline-heading">Health reserve</h3>
              <button
                type="button"
                className="lgc-icon-btn"
                aria-label="Remove health reserve"
                onClick={() => {
                  updateHealth({
                    preMedicareMonthlyPremium: 0,
                    yearsOfCoverageNeeded: 0,
                    hsaAnnualContribution: 0,
                    deductibleBufferTarget: 0,
                  });
                  setShowHealth(false);
                }}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="lgc-field-grid lgc-field-grid--2">
              <NumField
                id="premium"
                label="Pre-Medicare /mo"
                prefix="$"
                value={plan.health.preMedicareMonthlyPremium}
                onChange={(v) => updateHealth({ preMedicareMonthlyPremium: v })}
              />
              <NumField
                id="coverage-yrs"
                label="Years before Medicare"
                value={plan.health.yearsOfCoverageNeeded}
                onChange={(v) => updateHealth({ yearsOfCoverageNeeded: v })}
              />
              <NumField
                id="hsa"
                label="HSA /yr"
                prefix="$"
                value={plan.health.hsaAnnualContribution}
                onChange={(v) => updateHealth({ hsaAnnualContribution: v })}
              />
            </div>
          </div>
        ) : (
          <button type="button" className="lgc-add-btn lgc-field--full" onClick={() => setShowHealth(true)}>
            <Plus size={14} /> Add health reserve
          </button>
        )}

        {showLifestyle ? (
          <div className="lgc-child-card lgc-field--full">
            <div className="lgc-child-header">
              <h3 className="lgc-inline-heading">Vacations & other</h3>
              <button
                type="button"
                className="lgc-icon-btn"
                aria-label="Remove lifestyle goals"
                onClick={() => {
                  updateLifestyle({ vacationsPerYear: 0, vacationCost: 0, otherGoals: [] });
                  setShowLifestyle(false);
                }}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="lgc-field-grid lgc-field-grid--2">
              <NumField
                id="vac-n"
                label="Vacations /yr"
                value={plan.lifestyle.vacationsPerYear}
                onChange={(v) => updateLifestyle({ vacationsPerYear: v })}
              />
              <NumField
                id="vac-cost"
                label="Cost each"
                prefix="$"
                value={plan.lifestyle.vacationCost}
                onChange={(v) => updateLifestyle({ vacationCost: v })}
              />
            </div>
            {plan.lifestyle.otherGoals.map((g) => (
              <div key={g.id} className="lgc-goal-row lgc-field--full">
                <input
                  className="lgc-field-input"
                  aria-label="Goal label"
                  value={g.label}
                  onChange={(e) =>
                    updateLifestyle({
                      otherGoals: plan.lifestyle.otherGoals.map((x) =>
                        x.id === g.id ? { ...x, label: e.target.value } : x,
                      ),
                    })
                  }
                />
                <NumField
                  id={`goal-${g.id}`}
                  label=""
                  prefix="$"
                  value={g.annualAmount}
                  onChange={(v) =>
                    updateLifestyle({
                      otherGoals: plan.lifestyle.otherGoals.map((x) =>
                        x.id === g.id ? { ...x, annualAmount: v } : x,
                      ),
                    })
                  }
                />
                <button
                  type="button"
                  className="lgc-icon-btn"
                  aria-label="Remove goal"
                  onClick={() =>
                    updateLifestyle({ otherGoals: plan.lifestyle.otherGoals.filter((x) => x.id !== g.id) })
                  }
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="lgc-add-btn lgc-field--full"
              onClick={() =>
                updateLifestyle({
                  otherGoals: [
                    ...plan.lifestyle.otherGoals,
                    { id: newGoalLineId(), label: 'New goal', annualAmount: 0 },
                  ],
                })
              }
            >
              <Plus size={14} /> Add annual goal
            </button>
          </div>
        ) : (
          <button type="button" className="lgc-add-btn lgc-field--full" onClick={() => setShowLifestyle(true)}>
            <Plus size={14} /> Add vacation / other goal
          </button>
        )}
      </div>
    );
  }

  const spendingTotalRetirementMonthly = retirementMonthlyFromTodayAnnual(
    spendingTotalToday,
    plan.profile.inflationPct,
    yearsToRetire,
  );

  return (
    <div className="lgc-field-grid">
      <p className="lgc-hint lgc-field--full">What will you spend per year in retirement? (today&apos;s $)</p>
      <NumField
        id="yrs-ret"
        label="Years in retirement"
        value={plan.retirement.yearsInRetirement}
        onChange={(v) => updateRetirement({ yearsInRetirement: v })}
      />
      <div className="lgc-spending-table lgc-field--full">
        <div className="lgc-spending-header">
          <span>Spending</span>
          <span>Today /mo</span>
          <span className="lgc-sr-only">Remove</span>
        </div>
        {plan.retirement.spendingCategories.map((cat) => {
          const todayMonthly = cat.annualAmountToday / 12;
          return (
            <div key={cat.id} className="lgc-spending-row lgc-spending-row--compact">
              <input
                className="lgc-spending-label"
                aria-label="Spending category name"
                value={cat.label}
                onChange={(e) => updateSpendingCategory(cat.id, { label: e.target.value })}
              />
              <div className="lgc-field-input-wrap lgc-spending-input">
                <span className="lgc-field-affix">$</span>
                <input
                  type="number"
                  className="lgc-field-input"
                  min={0}
                  step={25}
                  aria-label={`${cat.label} monthly spending today`}
                  value={todayMonthly ? Math.round(todayMonthly) : ''}
                  onChange={(e) =>
                    updateSpendingCategory(cat.id, {
                      annualAmountToday: Math.max(0, Math.round((parseFloat(e.target.value) || 0) * 12)),
                    })
                  }
                />
              </div>
              <button
                type="button"
                className="lgc-icon-btn"
                onClick={() => removeSpendingCategory(cat.id)}
                aria-label={`Remove ${cat.label}`}
                disabled={plan.retirement.spendingCategories.length <= 1}
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
        <div className="lgc-spending-row lgc-spending-row--total">
          <span className="lgc-spending-total-label">Total</span>
          <span className="lgc-mono">{formatCurrency(spendingTotalTodayMonthly)}/mo</span>
          <span />
        </div>
      </div>
      <button type="button" className="lgc-add-btn lgc-field--full" onClick={addSpendingCategory}>
        <Plus size={14} /> Add spending category
      </button>
      <p className="lgc-hint lgc-field--full">
        Nest egg needed at {plan.profile.retirementAge}: {formatCurrency(corpusAtRetirement)} · about{' '}
        {formatCurrency(spendingTotalRetirementMonthly)}/mo then.
      </p>
      <LgcAccountsSection plan={plan} sourcePlan={planForEdits} onUpdate={onUpdate} compact />
    </div>
  );
};

export default LgcSectionContent;
